import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Share, 
  AlertTriangle, 
  BarChart3, 
  Download, 
  Award, 
  Trophy, 
  Star, 
  Target, 
  BookOpen, 
  TrendingUp, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock 
} from 'lucide-react';

import { useRealtime } from '../../contexts/RealtimeContext';
import { QuestionComponent } from '../../components/QuestionTypes';
import KatexRenderer from '../../components/KatexRenderer';
import EnhancedTestResults from '../../components/EnhancedTestResults';
import TestAnalysisModal from '../../components/TestAnalysisModal';
import { calculateRealTestResults, calculateTestStatistics, calculateRankAndPercentile } from '../../utils/realTestResultsCalculator';
import { getTestById, getTestResultsByTestId, getTestStatistics, getTestPerformanceComparison, getTestResultById } from '../../firebase/collections';
import { calculateSectionalResultsFromStoredData } from '../../utils/sectionalAnalysis';

// Check if user answer is correct based on question type
const checkAnswerCorrectness = (question, userAnswer) => {
  if (userAnswer === null || userAnswer === undefined || userAnswer === '') {
    return false;
  }

  const { type, correctAnswer } = question;

  switch (type) {
    case 'multiple-choice':
      return userAnswer === correctAnswer;
    
    case 'multiple-choice-multi':
      if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
        return userAnswer.length === correctAnswer.length &&
               userAnswer.every(ans => correctAnswer.includes(ans));
      }
      return false;
    
    case 'integer':
    case 'floating-point':
      return parseFloat(userAnswer) === parseFloat(correctAnswer);
    
    case 'subjective':
      return String(userAnswer).toLowerCase().trim() === String(correctAnswer).toLowerCase().trim();
    
    case 'matrix-match':
      if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
        return JSON.stringify(userAnswer.sort()) === JSON.stringify(correctAnswer.sort());
      }
      return false;
    
    case 'assertion-reason':
      return userAnswer === correctAnswer;
    
    default:
      return userAnswer === correctAnswer;
  }
};

const TestResults = () => {
  const { testId } = useParams();
  // React Router automatically decodes URL parameters
  // So if URL has "ssc%20cgl", useParams() returns "ssc cgl"
  // Use it as-is since Firebase document IDs can contain spaces
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, isConnected, connectionStatus } = useRealtime();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Get results from navigation state or find from Firebase data
  const [results, setResults] = useState(null);
  const [useEnhancedView, setUseEnhancedView] = useState(true);
  const [enhancedResults, setEnhancedResults] = useState(null);
  const [loadingEnhanced, setLoadingEnhanced] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [testDataForAnalysis, setTestDataForAnalysis] = useState(null);

  // Prepare analysis data when modal opens
  useEffect(() => {
    if (showAnalysisModal && results) {
      const prepareAnalysisData = async () => {
        try {
          // If we don't have questions in results, try to fetch from test data
          let questions = results.questions || [];
          let fetchedTestData = null;
          
          if (results.testId) {
            const testDataResponse = await getTestById(results.testId);
            if (testDataResponse.success && testDataResponse.data) {
              fetchedTestData = testDataResponse.data;
              if (questions.length === 0) {
                questions = testDataResponse.data.questions || testDataResponse.data.originalTestData?.questions || [];
              }
            }
          }
          
          setTestDataForAnalysis(fetchedTestData);
          setAnalysisData({
            ...results,
            questions: questions,
            answers: results.answers || {},
            detailedResults: results.detailedResults || [],
            score: results.score || results.totalScore || 0,
            totalMarks: results.totalMarks || 100,
            correctAnswers: results.correctAnswers || 0,
            wrongAnswers: results.wrongAnswers || 0,
            unattempted: results.unattempted || 0,
            accuracy: results.accuracy || 0,
            timeSpent: results.timeSpent || results.totalTimeSpent || 0,
            testName: results.testName || 'Test',
            completedAt: results.completedAt || new Date(),
            sectionalResults: results.sectionalResults || []
          });
        } catch (error) {
          setTestDataForAnalysis(null);
          setAnalysisData({
            ...results,
            questions: results.questions || [],
            answers: results.answers || {},
            detailedResults: results.detailedResults || [],
            score: results.score || results.totalScore || 0,
            totalMarks: results.totalMarks || 100,
            correctAnswers: results.correctAnswers || 0,
            wrongAnswers: results.wrongAnswers || 0,
            unattempted: results.unattempted || 0,
            accuracy: results.accuracy || 0,
            timeSpent: results.timeSpent || results.totalTimeSpent || 0,
            testName: results.testName || 'Test',
            completedAt: results.completedAt || new Date(),
            sectionalResults: results.sectionalResults || []
          });
        }
      };
      
      prepareAnalysisData();
    }
  }, [showAnalysisModal, results]);

  // Load results data
  useEffect(() => {
    const loadResults = async () => {
      if (location.state) {
        // Use data passed from navigation (immediate test results)
        setResults(location.state);
        setLoading(false);
      } else if (testId && userData.testResults) {
        // Find test result from Firebase data
        const testResult = userData.testResults.find(result => result.id === testId);
        if (testResult) {
          setResults(testResult);
        } else {
          // If not found in userData, try to fetch from Firebase directly
          try {
            const resultData = await getTestResultById(testId);
            if (resultData.success && resultData.data) {
              setResults(resultData.data);
            }
          } catch (error) {
            // Error fetching test result
          }
        }
        setLoading(false);
      } else if (testId && isConnected) {
        // If userData.testResults is not available but we have testId, try to fetch directly
        try {
          const resultData = await getTestResultById(testId);
          if (resultData.success && resultData.data) {
            setResults(resultData.data);
          }
        } catch (error) {
          // Error fetching test result
        }
        setLoading(false);
      } else if (isConnected) {
        setLoading(false);
      }
    };

    loadResults();
  }, [location.state, testId, userData.testResults, isConnected]);

  // Load enhanced results when needed
  useEffect(() => {
    if (useEnhancedView && results) {
      setLoadingEnhanced(true);
      getEnhancedResults().then(enhancedData => {
        setEnhancedResults(enhancedData);
        setLoadingEnhanced(false);
      }).catch(error => {
        setLoadingEnhanced(false);
      });
    } else {
      setEnhancedResults(null);
    }
  }, [useEnhancedView, results]);

  // Generate enhanced results data if needed
  const getEnhancedResults = async () => {
    // Validate that results has minimum required data
    if (!results) {
      return null;
    }
    
    // Check if we have at least some score data
    const hasScoreData = results.score !== undefined || results.totalScore !== undefined;
    const hasAnswerData = results.correctAnswers !== undefined || results.wrongAnswers !== undefined;
    
    if (hasScoreData || hasAnswerData) {
      try {
        // Get comprehensive test performance comparison data
        const performanceData = await getTestPerformanceComparison(results.testId, results.userId, results.score);
        
        if (!performanceData.success) {
          // Failed to fetch performance comparison data
        }

        // Use fallback data if analytics fail
        const analytics = performanceData.success ? performanceData.data.analytics : {
          totalAttempts: 1,
          lastUpdated: new Date(),
          basicStats: {
            averageScore: results.score || results.totalScore || 0,
            medianScore: results.score || results.totalScore || 0,
            highestScore: results.score || results.totalScore || 0,
            lowestScore: results.score || results.totalScore || 0,
            averageAccuracy: results.accuracy || 0,
            averageTime: results.totalTimeSpent || 0,
            averageCorrect: results.correctAnswers || 0,
            averageWrong: results.wrongAnswers || 0,
            averageUnattempted: results.unattempted || 0
          },
          marksDistribution: [],
          sectionalStats: [],
          percentileRanges: [],
          timeAnalysis: {
            averageTimePerQuestion: timeAnalysis.avgTime || 0,
            fastestCompletion: timeAnalysis.minTime || 0,
            slowestCompletion: timeAnalysis.maxTime || 0,
            timeDistribution: []
          }
        };
        // Don't use fallback values - if performance data fails, we need to calculate rank properly
        let userRank = performanceData.success ? performanceData.data.userRank : null;
        let topPerformers = performanceData.success ? performanceData.data.topPerformers : [];
        
        // If performance data failed, calculate rank manually
        if (!userRank) {
          try {
            const allResultsResult = await getTestResultsByTestId(results.testId);
            if (allResultsResult.success && allResultsResult.data.length > 0) {
              const allScores = allResultsResult.data.map(r => r.score);
              const { rank, totalParticipants, percentile } = calculateRankAndPercentile(results.score, allScores);
              userRank = { rank, totalParticipants, percentile };
            }
          } catch (error) {
          }
        }

        // Get test configuration and data
        const testData = await getTestById(results.testId);
        const actualTestData = testData.success ? testData.data : null;
        const testConfig = results.testConfig || (actualTestData ? {
          useDynamicSections: actualTestData.useDynamicSections,
          sections: actualTestData.sections || []
        } : null);
        
        // Merge questions from test data if not present in results
        // Also merge user answers and detailed results into questions for sectional analysis
        let mergedQuestions = results.questions || actualTestData?.questions || [];
        
        // If we have questions from test data but no user answers in them, merge the answers
        if (mergedQuestions.length > 0 && results.answers && results.detailedResults) {
          
          mergedQuestions = mergedQuestions.map((question, index) => {
            // Try to find user answer by question ID first, then by index
            let userAnswer = results.answers[question.id];
            let detailedResult = results.detailedResults[index];
            
            // If not found by ID, try to find by index in answers object
            if (userAnswer === undefined && results.answers) {
              const answerKeys = Object.keys(results.answers);
              if (answerKeys[index]) {
                userAnswer = results.answers[answerKeys[index]];
              }
            }
            
      
            
            return {
              ...question,
              userAnswer: userAnswer,
              isCorrect: detailedResult?.isCorrect,
              timeSpent: detailedResult?.timeSpent || 0
            };
          });
        }
        
        const mergedResults = {
          ...results,
          questions: mergedQuestions
        };
        

        
        let sectionalResults = results.sectionalResults;
        
        // If no sectional results, calculate them from stored data
        if (!sectionalResults || sectionalResults.length === 0) {
          if (testConfig && testConfig.sections && testConfig.sections.length > 0 && mergedResults.questions && mergedResults.questions.length > 0) {
            // Use the new function to calculate sectional results from stored data
            sectionalResults = calculateSectionalResultsFromStoredData(testConfig, mergedResults.questions, {
              score: results.score || 0,
              totalMarks: results.totalMarks || 0,
              correctAnswers: results.correctAnswers || 0,
              wrongAnswers: results.wrongAnswers || 0,
              unattempted: results.unattempted || 0,
              timeSpent: results.totalTimeSpent || 0
            });
          }
        }
        
        // Generate sectional statistics for comparison
        const sectionalStats = sectionalResults ? sectionalResults.map(section => ({
          name: section.name,
          totalAttempts: 1,
          averageScore: section.score,
          averageAccuracy: section.accuracy,
          averageTime: section.timeSpent,
          highestScore: section.score,
          lowestScore: section.score,
          averageCorrect: section.correctAnswers,
          averageWrong: section.wrongAnswers,
          fastestTime: section.timeSpent,
          highestAccuracy: section.accuracy
        })) : [];
        
        // Calculate totalTime properly before creating the object
        // Duration is typically stored in minutes, so convert to seconds
        // But check if it's already in seconds (if > 3600, it's likely already in seconds)
        const durationValue = actualTestData?.duration || results.totalTime || 60;
        let calculatedTotalTime = 60; // Default 1 minute in seconds
        if (typeof durationValue === 'number' && durationValue > 0) {
          if (durationValue > 3600) {
            // Already in seconds (more than 1 hour)
            calculatedTotalTime = durationValue;
          } else if (durationValue === 1) {
            // 1 minute = 60 seconds
            calculatedTotalTime = 60;
          } else if (durationValue < 60) {
            // Less than 60, likely minutes, convert to seconds
            calculatedTotalTime = durationValue * 60;
          } else {
            // Between 60 and 3600, could be seconds or minutes
            // If it's a round number divisible by 60, likely minutes
            if (durationValue % 60 === 0 && durationValue <= 600) {
              // Likely minutes (e.g., 60, 120, 240 minutes)
              calculatedTotalTime = durationValue * 60;
            } else {
              // Already in seconds
              calculatedTotalTime = durationValue;
            }
          }
        }
        
        // Ensure all required fields are properly populated
        const enhancedData = {
          ...mergedResults,
          // Ensure score is properly set
          score: results.score || results.totalScore || 0,
          totalScore: results.totalScore || results.score || 0,
          // Ensure marks are properly set
          totalMarks: actualTestData?.totalMarks || results.totalMarks || (mergedResults.questions?.length * 4) || 100,
          // Ensure time is properly set (convert to seconds if needed)
          totalTime: calculatedTotalTime,
          // Ensure questions count is properly set
          totalQuestions: mergedResults.questions?.length || actualTestData?.totalQuestions || results.totalQuestions || 0,
          // Ensure answers are properly set
          correctAnswers: results.correctAnswers || 0,
          wrongAnswers: results.wrongAnswers || 0,
          unattempted: results.unattempted || 0,
          // Ensure accuracy is properly calculated
          accuracy: results.accuracy || (results.correctAnswers && mergedResults.questions?.length 
            ? Math.round((results.correctAnswers / mergedResults.questions.length) * 100) 
            : 0),
          // Ensure time spent is properly set
          timeSpent: results.timeSpent || results.totalTimeSpent || 0,
          totalTimeSpent: results.totalTimeSpent || results.timeSpent || 0,
          // Ensure rank and percentile are properly set
          rank: userRank?.rank || results.rank || 0,
          totalParticipants: userRank?.totalParticipants || results.totalParticipants || 0,
          percentile: userRank?.percentile || results.percentile || 0,
          // Ensure sectional results are properly set
          sectionalResults: sectionalResults || [],
          testConfig: testConfig,
          cutOff: actualTestData?.cutOff || results.cutOff || 55,
          // Add comprehensive analytics
          marksDistribution: analytics?.marksDistribution || [],
          averageScore: analytics?.basicStats?.averageScore || results.score || results.totalScore || 0,
          medianScore: analytics?.basicStats?.medianScore || results.score || results.totalScore || 0,
          averageAccuracy: analytics?.basicStats?.averageAccuracy || results.accuracy || 0,
          averageTime: analytics?.basicStats?.averageTime || results.totalTimeSpent || results.timeSpent || 0,
          averageCorrect: analytics?.basicStats?.averageCorrect || results.correctAnswers || 0,
          averageWrong: analytics?.basicStats?.averageWrong || results.wrongAnswers || 0,
          averageUnattempted: analytics?.basicStats?.averageUnattempted || results.unattempted || 0,
          totalAttempts: analytics?.totalAttempts || 1,
          highestScore: analytics?.basicStats?.highestScore || results.score || results.totalScore || 0,
          lowestScore: analytics?.basicStats?.lowestScore || results.score || results.totalScore || 0,
          sectionalStats: sectionalStats,
          percentileRanges: analytics?.percentileRanges || [],
          timeAnalysis: analytics?.timeAnalysis || {},
          topPerformers: topPerformers
        };
        
        return enhancedData;
      } catch (error) {
        // Return a fallback enhanced result with better data validation
        return {
          ...results,
          // Ensure score is properly set
          score: results.score || results.totalScore || 0,
          totalScore: results.totalScore || results.score || 0,
          // Ensure marks are properly set
          totalMarks: results.totalMarks || (results.questions?.length * 4) || 100,
          // Ensure time is properly set (convert to seconds if needed)
          totalTime: typeof (results.totalTime || 60) === 'number' 
            ? (results.totalTime || 60) * 60 
            : (results.totalTime || 60),
          // Ensure questions count is properly set
          totalQuestions: results.questions?.length || results.totalQuestions || 0,
          // Ensure answers are properly set
          correctAnswers: results.correctAnswers || 0,
          wrongAnswers: results.wrongAnswers || 0,
          unattempted: results.unattempted || 0,
          // Ensure accuracy is properly calculated
          accuracy: results.accuracy || (results.correctAnswers && results.questions?.length 
            ? Math.round((results.correctAnswers / results.questions.length) * 100) 
            : 0),
          // Ensure time spent is properly set
          timeSpent: results.timeSpent || results.totalTimeSpent || 0,
          totalTimeSpent: results.totalTimeSpent || results.timeSpent || 0,
          // Ensure rank and percentile are properly set
          rank: results.rank || 0,
          totalParticipants: results.totalParticipants || 0,
          percentile: results.percentile || 0,
          // Ensure sectional results are properly set
          sectionalResults: [],
          testConfig: null,
          cutOff: results.cutOff || 55,
          marksDistribution: [],
          averageScore: results.score || results.totalScore,
          medianScore: results.score || results.totalScore,
          averageAccuracy: results.accuracy,
          averageTime: results.totalTimeSpent,
          averageCorrect: results.correctAnswers,
          averageWrong: results.wrongAnswers,
          averageUnattempted: results.unattempted,
          totalAttempts: 1,
          highestScore: results.score || results.totalScore,
          lowestScore: results.score || results.totalScore,
          sectionalStats: [],
          percentileRanges: [],
          timeAnalysis: {},
          topPerformers: []
        };
      }
    }
    
    // Return null if no results or insufficient data
    return null;
  };

  // Loading state
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connecting to real-time data...</p>
          <p className="text-sm text-gray-500 mt-2">Status: {connectionStatus}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading test results...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Award className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Test Result Not Found</h3>
          <p className="text-gray-600 mb-4">The test result you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/dashboard/test-results')}
            className="btn-primary"
          >
            Back to Test Results
          </button>
        </div>
      </div>
    );
  }

  const { 
    score = 0,
    totalScore = 0,
    totalMarks = 100,
    percentageScore = 0, 
    totalQuestions = 0, 
    correctAnswers = 0, 
    wrongAnswers = 0, 
    unattempted = 0,
    accuracy = 0,
    detailedResults = [],
    totalTimeSpent: rawTotalTimeSpent = 0,
    answers = {}, 
    questions = [],
    testName = 'Test',
    testId: actualTestId = testId
  } = results || {};

  // Calculate totalTimeSpent from detailedResults if not available
  const totalTimeSpent = rawTotalTimeSpent || (detailedResults && detailedResults.length > 0 
    ? detailedResults.reduce((sum, r) => sum + (r.timeSpent || 0), 0)
    : 0);

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return 'Excellent! Outstanding performance!';
    if (score >= 80) return 'Great job! Well done!';
    if (score >= 70) return 'Good work! Keep practicing!';
    if (score >= 60) return 'Not bad! Room for improvement.';
    return 'Keep practicing! You can do better!';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <Trophy className="h-8 w-8 text-green-600" />;
    if (score >= 80) return <Star className="h-8 w-8 text-blue-600" />;
    if (score >= 70) return <Target className="h-8 w-8 text-yellow-600" />;
    return <AlertTriangle className="h-8 w-8 text-red-600" />;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const getDifficultyStats = () => {
    const stats = { easy: { correct: 0, total: 0 }, medium: { correct: 0, total: 0 }, hard: { correct: 0, total: 0 } };
    
    detailedResults?.forEach(result => {
      const difficulty = result.difficulty?.toLowerCase() || 'medium';
      if (stats[difficulty]) {
        stats[difficulty].total++;
        if (result.isCorrect) {
          stats[difficulty].correct++;
        }
      }
    });
    
    return stats;
  };

  const getTopicStats = () => {
    const stats = {};
    
    detailedResults?.forEach(result => {
      const topic = result.topic || 'Unknown';
      if (!stats[topic]) {
        stats[topic] = { correct: 0, total: 0 };
      }
      stats[topic].total++;
      if (result.isCorrect) {
        stats[topic].correct++;
      }
    });
    
    return stats;
  };

  const getTimeAnalysis = () => {
    if (!detailedResults || detailedResults.length === 0) return {};
    
    const times = detailedResults.map(r => r.timeSpent || 0).filter(t => t > 0);
    
    // Calculate average time per question from total time if individual times not available
    let avgTime = 0;
    let minTime = 0;
    let maxTime = 0;
    
    if (times.length === 0) {
      // Fallback: use total time divided by number of questions
      const totalTime = totalTimeSpent || 0;
      const totalQuestions = detailedResults.length;
      avgTime = totalQuestions > 0 ? Math.round(totalTime / totalQuestions) : 0;
      minTime = 0;
      maxTime = 0;
    } else {
      avgTime = Math.round(times.reduce((sum, t) => sum + t, 0) / times.length);
      minTime = Math.min(...times);
      maxTime = Math.max(...times);
    }
    
    return { 
      avgTime, 
      minTime, 
      maxTime,
      totalTrackedTime: times.reduce((sum, t) => sum + t, 0),
      questionsWithTime: times.length,
      totalQuestions: detailedResults.length
    };
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'review', name: 'Question Review', icon: BookOpen },
    { id: 'analysis', name: 'Detailed Analysis', icon: TrendingUp },
    ...(results.sectionalResults && results.sectionalResults.length > 0 ? [{ id: 'sections', name: 'Section Analysis', icon: Target }] : [])
  ];

  const difficultyStats = getDifficultyStats();
  const topicStats = getTopicStats();
  const timeAnalysis = getTimeAnalysis();

  if (useEnhancedView) {
    if (loadingEnhanced) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading enhanced results...</p>
          </div>
        </div>
      );
    }

    if (enhancedResults) {
      return (
        <EnhancedTestResults
          results={enhancedResults}
          onBack={() => navigate('/dashboard/mock-tests')}
          onTakeAnotherTest={() => navigate('/dashboard/mock-tests')}
          onPracticeWeakAreas={() => navigate('/dashboard/revision')}
          testType={results?.testType || 'Mock Test'}
        />
      );
    }

    // If enhanced view is enabled but no enhanced results, show loading or fallback
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Preparing enhanced results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard/mock-tests')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Test Results
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {testName || 'Mock Test'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setUseEnhancedView(!useEnhancedView)}
              className={`inline-flex items-center ${useEnhancedView ? 'btn-secondary' : 'btn-primary'}`}
            >
              <BarChart3 size={16} className="mr-2" />
              {useEnhancedView ? 'Regular View' : 'Enhanced View'}
            </button>
            <button 
              onClick={() => setShowAnalysisModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <BarChart3 size={16} className="mr-2" />
              Analysis
            </button>
            <button className="btn-secondary inline-flex items-center">
              <Share size={16} className="mr-2" />
              Share
            </button>
            <button className="btn-secondary inline-flex items-center">
              <Download size={16} className="mr-2" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center mb-8"
        >
          <div className="flex items-center justify-center mb-6">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${getScoreColor(percentageScore)}`}>
              {getScoreIcon(percentageScore)}
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {percentageScore}%
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
            {getScoreMessage(percentageScore)}
          </p>
          
          <div className="flex items-center justify-center space-x-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{correctAnswers}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Correct</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{wrongAnswers}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Wrong</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{unattempted}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Unattempted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{accuracy}%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Accuracy</div>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Correct Answers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{correctAnswers}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="text-red-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Wrong Answers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{wrongAnswers}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="text-blue-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Accuracy</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{accuracy}%</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Clock className="text-purple-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Time Taken</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatTime(totalTimeSpent || 0)}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="card p-6">
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon size={16} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Performance Chart */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Performance Overview
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600 dark:text-gray-400">Correct Answers</span>
                          <span className="font-medium text-gray-900 dark:text-white">{correctAnswers}/{totalQuestions}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-green-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${(correctAnswers / totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600 dark:text-gray-400">Wrong Answers</span>
                          <span className="font-medium text-gray-900 dark:text-white">{wrongAnswers}/{totalQuestions}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-red-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${(wrongAnswers / totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600 dark:text-gray-400">Unattempted</span>
                          <span className="font-medium text-gray-900 dark:text-white">{unattempted}/{totalQuestions}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-gray-400 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${(unattempted / totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Recommendations
                    </h3>
                    <div className="space-y-3">
                      {percentageScore < 80 && (
                        <div className="flex items-start space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                          <Star className="text-yellow-600 mt-1" size={16} />
                          <div>
                            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                              Practice More
                            </p>
                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                              Focus on the topics where you made mistakes
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <BookOpen className="text-blue-600 mt-1" size={16} />
                        <div>
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Review Questions
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            Go through the question review to understand your mistakes
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <TrendingUp className="text-green-600 mt-1" size={16} />
                        <div>
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">
                            Take More Tests
                          </p>
                          <p className="text-xs text-green-700 dark:text-green-300">
                            Practice with similar tests to improve your score
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Question Review Tab */}
            {activeTab === 'review' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {questions?.map((question, index) => {
                  const userAnswer = answers[question.id];
                  const isAttempted = userAnswer !== null && userAnswer !== undefined && userAnswer !== '' && userAnswer !== 'null' && userAnswer !== 'undefined';
                  const isCorrect = isAttempted ? checkAnswerCorrectness(question, userAnswer) : false;
                  const questionTime = detailedResults?.[index]?.timeSpent || 0;
                  
                  return (
                    <div key={question.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          Question {index + 1}
                        </h3>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Clock size={14} />
                            <span>{formatTime(questionTime)}</span>
                          </div>
                          {isCorrect ? (
                            <div className="flex items-center space-x-1 text-green-600">
                              <CheckCircle size={16} />
                              <span className="text-sm font-medium">Correct</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1 text-red-600">
                              <XCircle size={16} />
                              <span className="text-sm font-medium">Incorrect</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <QuestionComponent
                          question={question}
                          selectedAnswer={userAnswer}
                          onAnswerSelect={() => {}}
                          isReview={true}
                          correctAnswer={question.correctAnswer}
                        />
                      </div>
                      
                      {!isCorrect && question.explanation && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                            Explanation
                          </h4>
                          <div className="text-sm text-blue-700 dark:text-blue-300">
                            <KatexRenderer content={question.explanation} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </motion.div>
            )}

            {/* Detailed Analysis Tab */}
            {activeTab === 'analysis' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {/* Sectional Analysis */}
                {results.sectionalResults && results.sectionalResults.length > 0 && (
                  <div className="card p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Sectional Performance
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {results.sectionalResults.map((section, idx) => (
                        <div key={section.id || idx} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">{section.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              section.accuracy >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              section.accuracy >= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {section.accuracy}%
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Score</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {section.score}/{section.totalMarks}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Correct</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {section.correctAnswers}/{section.totalQuestions}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Time</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatTime(section.timeSpent || 0)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(section.correctAnswers / section.totalQuestions) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Time Analysis */}
                  <div className="card p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Time Analysis
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Total time taken</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatTime(totalTimeSpent || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Average time per question</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {timeAnalysis.avgTime ? formatTime(timeAnalysis.avgTime) : '0:00'}
                        </span>
                      </div>
                      {timeAnalysis.minTime > 0 && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Fastest question</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatTime(timeAnalysis.minTime)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Slowest question</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatTime(timeAnalysis.maxTime)}</span>
                          </div>
                        </>
                      )}
                      {timeAnalysis.questionsWithTime > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-500">Time tracked for</span>
                          <span className="text-gray-500 dark:text-gray-500">
                            {timeAnalysis.questionsWithTime} / {timeAnalysis.totalQuestions} questions
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Difficulty Analysis */}
                  <div className="card p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Difficulty Analysis
                    </h3>
                    <div className="space-y-4">
                      {Object.entries(difficultyStats).map(([difficulty, stats]) => (
                        <div key={difficulty} className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400 capitalize">{difficulty}</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {stats.correct}/{stats.total} ({stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Topic Analysis */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Topic-wise Performance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(topicStats).map(([topic, stats]) => (
                      <div key={topic} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">{topic}</h4>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Score</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {stats.correct}/{stats.total}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Accuracy</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Sections Tab */}
            {activeTab === 'sections' && results.sectionalResults && results.sectionalResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {results.sectionalResults.map((section, idx) => (
                    <div key={section.id || idx} className="card p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {section.name}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          section.accuracy >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          section.accuracy >= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {section.accuracy}% Accuracy
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Performance Overview */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{section.score}</div>
                            <div className="text-sm text-blue-700 dark:text-blue-300">Score</div>
                          </div>
                          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{section.correctAnswers}</div>
                            <div className="text-sm text-green-700 dark:text-green-300">Correct</div>
                          </div>
                        </div>
                        
                        {/* Detailed Stats */}
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Total Questions</span>
                            <span className="font-medium text-gray-900 dark:text-white">{section.totalQuestions}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Correct Answers</span>
                            <span className="font-medium text-gray-900 dark:text-white">{section.correctAnswers}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Wrong Answers</span>
                            <span className="font-medium text-gray-900 dark:text-white">{section.wrongAnswers}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Unattempted</span>
                            <span className="font-medium text-gray-900 dark:text-white">{section.unattempted}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Time Spent</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatTime(section.timeSpent || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Total Marks</span>
                            <span className="font-medium text-gray-900 dark:text-white">{section.score}/{section.totalMarks}</span>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600 dark:text-gray-400">Performance</span>
                            <span className="font-medium text-gray-900 dark:text-white">{section.correctAnswers}/{section.totalQuestions}</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                            <div 
                              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${(section.correctAnswers / section.totalQuestions) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mt-8">
                          <button
                  onClick={() => navigate('/dashboard/mock-tests')}
                  className="btn-secondary inline-flex items-center"
                >
            <BookOpen size={16} className="mr-2" />
            Take Another Test
          </button>
          <button
            onClick={() => navigate('/dashboard/revision')}
            className="btn-primary inline-flex items-center"
          >
            <TrendingUp size={16} className="mr-2" />
            Practice Weak Areas
          </button>
        </div>
      </div>

      {/* Test Analysis Modal */}
      {showAnalysisModal && analysisData && (
        <TestAnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          testResult={analysisData}
          testData={testDataForAnalysis}
        />
      )}
    </div>
  );
};

export default TestResults;
